import { sazkaAlert } from "@/shared/composables/use-sazka-alert";
import * as UrlUtils from "@/shared/composables/use-url-parms-helper";
import { NgBalance, NgFw, NgSessionLite } from "@/shared/model/ng/ng-fw";
import { useNgSession } from "@/shared/ng/ng-session";
import { ref } from "vue";
import { logLogin } from "./log-login";
import { ngHandleMmi } from "@/shared/ng/ng-mmi";
import { useNg3DsRedirection } from "./ng-3ds-redirection";
import { useChat } from "../composables/use-chat";
import { logException } from "@/shared/utils/logger/use-app-logger";
import useSazkaSnackbar from "@/shared/composables/use-sazka-snackbar";
import analytics from "../analytics";
import { useAppInsights } from "../utils/logger/app-insights";

export const ngFrameworkLoadedSuccessfully = ref(false);
export const isNgUIVisible = ref(false);

let ngFrameworkError = false;

const frameworkLoadedPromiseResolves: (() => void)[] = [];
const sessionStatusCallbacks: ((session: NgSessionLite) => void)[] = [];
const loginAbortedCallbacks: (() => void)[] = [];
let openPageCallback: (key: string, customInfo: string | any) => void = null;

export async function useNg(openPage: typeof openPageCallback) {
    openPageCallback = openPage;
    useNgSession().init();
    useNgLoader();
    await ngFrameworkLoaded();
}

/**
 * ng_hs is ng framework's way to notify web page when user state changes
 * it is using callbacks
 */
async function useNgHs() {
    try {
        ng_hs.onBalanceUpdated((balance: NgBalance) => {
            useNgSession().balance.value = balance;
        });

        ng_hs.onSessionStatusUpdate((session: NgSessionLite) => {
            if (session.Status == "Login") {
                // we need to do that to load more information from NG framework
                useNgSession().refreshSession();
                logLogin();
                //Sazkabet Snackbar - handle before NX integration
                useSazkaSnackbar().createSnackbar({
                    message: "Herní limity",
                    type: "success",
                    duration: 4000,
                    icon: "settings",
                    button: {
                        label: "Nastavit",
                        onClick: () => {
                            window.location.href = `${environment.portalBaseUrl}?rg-limits=true`;
                        },
                    },
                });
                useAppInsights().setUserContext(session.PlayerID);
            } else {
                useNgSession().clearSession();
                useAppInsights().clearUserContext();
            }
            sessionStatusCallbacks.forEach((f) => f(session));
        });

        ng_hs.loginAborted(() => loginAbortedCallbacks.forEach((f) => f()));
        ng_hs.registrationAborted(() => loginAbortedCallbacks.forEach((f) => f()));

        ng_hs.openPage(openPageCallback);

        ng_hs.clearURLParams((urlParams: string[]) => {
            UrlUtils.clearUrlParams(urlParams);
        });

        ng_hs.onShowChat(() => {
            useChat().show();
        });

        ng_hs.onStateChange((state) => {
            isNgUIVisible.value = state === "active";
        });

        useNg3DsRedirection().registerNgHsCallback();
    } catch (e) {
        logException("neogames", e, { message: "An error has occurred while adding ng_hs callbacks" });
    }
}

export function getNgTimeDuration(ngActionTimestamp: string, ngServerTime: string): number {
    const start = Date.parse(ngActionTimestamp);
    const server = Date.parse(ngServerTime);
    const now = Date.now();
    return start - server + now;
}

function useNgIdentityVerification() {
    const parameter = UrlUtils.getQueryString("identityverification", null, false);
    if (parameter) {
        ng_fw.showPersonalInfoVerification();
    }
}

function useNgLoader() {
    if (ngLoader.isNgReady()) {
        frameworkLoaded();
    } else if (ngLoader.isNgError()) {
        frameworkLoadError(ngLoader.ngErrorMessage);
    } else {
        document.addEventListener("fwInitError", (reason) => frameworkLoadError(reason as any));
        document.addEventListener("fwReady", frameworkLoaded);
    }
}

export function ngFrameworkLoaded(): Promise<void> {
    return new Promise((res, rej) => {
        if (ngFrameworkLoadedSuccessfully.value) {
            res();
        } else if (ngFrameworkError) {
            rej();
        } else {
            frameworkLoadedPromiseResolves.push(res);
        }
    });
}

/**
 * @deprecated Use watch on useNgSession().isLoggedIn instead
 * @param f callback
 */
export function addSessionStatusUpdateCallback(f: (status: NgSessionLite) => void): void {
    sessionStatusCallbacks.push(f);
}

/**
 * @param f callback
 */
export function addLoginAbortedCallback(f: () => void): void {
    loginAbortedCallbacks.push(f);
}

/**
 * @param f callback to remove
 */
export function removeLoginAbortedCallback(f: () => void): void {
    const index = loginAbortedCallbacks.indexOf(f);
    if (index >= 0) {
        loginAbortedCallbacks.splice(index, 1);
    }
}

function frameworkLoaded() {
    ng_fw.keepAlive();
    ngFrameworkLoadedSuccessfully.value = true;
    useNgHs();
    ngHandleMmi();
    useNgIdentityVerification();

    const { refreshSession, refreshBalance } = useNgSession();
    refreshSession();
    refreshBalance();
    // We don't use restoreFromWebView() on Sazkabet
    //restoreFromWebView().then(() => {
    for (const resolve of frameworkLoadedPromiseResolves) {
        resolve();
    }
    //});
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function frameworkLoadError(errorMessage: string) {
    ngFrameworkLoadedSuccessfully.value = false;
    ngFrameworkError = true;
    sazkaAlert({
        title: "Tady něco nehraje…",
        subText:
            "Je nám líto, ale nepodařilo se správně načíst stránku a není možné využívat herní účet a sázet online. Zkuste stránku obnovit nebo v nastavení prohlížeče povolit cookies třetích stran. Pokud i poté potíže přetrvávají, jedná se o chybu na naší straně, na jejímž odstranění pracujeme.",
    });
    analytics.sendErrorEvent(
        "Je nám líto, ale nepodařilo se správně načíst stránku a není možné využívat herní účet a sázet online. Zkuste stránku obnovit nebo v nastavení prohlížeče povolit cookies třetích stran. Pokud i poté potíže přetrvávají, jedná se o chybu na naší straně, na jejímž odstranění pracujeme.",
        "pop-up",
    );
}

export function ngAction(actionName: keyof NgFw) {
    if (ngFrameworkLoadedSuccessfully.value) {
        (ng_fw[actionName] as any)();
    }
}
